// src/components/zoom/ZoomSyncModal.jsx
import React, { useEffect, useState } from 'react';
import { FiX, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { syncZoomAccount, zoomErrorMessage } from '../../services/zoomApiService';

const kindLabel = (k) => (k === 'masterclass' ? 'MC' : 'Clase');

const Section = ({ title, tone, items, render, selected, toggle, idOf }) => items.length === 0 ? null : (
  <div>
    <p className={`text-xs font-bold uppercase mb-1.5 ${tone}`}>{title} ({items.length})</p>
    <div className="border border-gray-100 rounded-lg divide-y divide-gray-50">
      {items.map(it => (
        <label key={idOf(it)} className="flex items-start gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
          <input type="checkbox" className="mt-0.5 accent-blue-600" checked={selected.has(idOf(it))} onChange={() => toggle(idOf(it))} />
          <span className="min-w-0">{render(it)}</span>
        </label>
      ))}
    </div>
  </div>
);

const ZoomSyncModal = ({ account, onClose }) => {
  const [diff, setDiff] = useState(null);
  const [error, setError] = useState('');
  const [sel, setSel] = useState({ updates: new Set(), missing: new Set(), imports: new Set() });
  const [applying, setApplying] = useState(false);

  const entryOf = (entryId) => [...(account.meetings || []), ...(account.claseMeetings || [])].find(m => m.id === entryId);
  const hasZoomId = (entryId) => { const e = entryOf(entryId); return !!String(e?.meetingId || e?.zoomNumericId || '').replace(/\D/g, ''); };
  const nameOf = (entryId) => entryOf(entryId)?.name || entryId;

  useEffect(() => {
    syncZoomAccount(account.id)
      .then(({ diff: d }) => {
        setDiff(d);
        // Por defecto: corregir sí; marcar solo las que tienen ID de Zoom; importar no (el usuario decide).
        setSel({
          updates: new Set(d.updates.map(u => u.entryId)),
          missing: new Set(d.missing.filter(m => hasZoomId(m.entryId)).map(m => m.entryId)),
          imports: new Set(),
        });
      })
      .catch(err => setError(zoomErrorMessage(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.id]);

  const toggler = (key) => (id) => setSel(prev => {
    const next = new Set(prev[key]);
    next.has(id) ? next.delete(id) : next.add(id);
    return { ...prev, [key]: next };
  });

  const total = sel.updates.size + sel.missing.size + sel.imports.size;
  const empty = diff && diff.updates.length + diff.missing.length + diff.imports.length === 0;

  const handleApply = async () => {
    setApplying(true);
    try {
      const { applied } = await syncZoomAccount(account.id, {
        updates: [...sel.updates], missing: [...sel.missing], imports: [...sel.imports],
      });
      toast.success(`Sincronizado: ${applied.verified} verificadas, ${applied.updates} corregidas, ${applied.missing} marcadas, ${applied.imports} importadas`);
      onClose();
    } catch (err) { toast.error(zoomErrorMessage(err), { duration: 7000 }); }
    finally { setApplying(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><FiRefreshCw /> Sincronizar con Zoom</h3>
            <p className="text-xs text-gray-500 mt-0.5">{account.name} · {account.email} — nada se borra en Zoom</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><FiX size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!diff && !error && <p className="text-sm text-gray-400 text-center py-8">Leyendo reuniones de Zoom…</p>}
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3">{error}</p>}
          {diff && diff.verified?.length > 0 && (
            <p className="text-sm text-green-700 bg-green-50 rounded-lg p-3">
              ✓ {diff.verified.length} sala(s) existen en Zoom.{empty ? ' Todo coincide, no hay nada que cambiar.' : ''} Al aplicar quedan marcadas como verificadas.
            </p>
          )}
          {empty && !diff.verified?.length && <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">No hay salas con ID de Zoom en esta cuenta.</p>}
          {diff && (
            <>
              <Section title="Corregir link/contraseña" tone="text-blue-600" items={diff.updates} idOf={u => u.entryId}
                selected={sel.updates} toggle={toggler('updates')}
                render={u => <><b>{kindLabel(u.kind)}</b> {nameOf(u.entryId)} <span className="block text-[11px] text-gray-500">{Object.entries(u.changes).map(([k, v]) => (k === 'zoomMissing' ? 'vuelve a estar disponible' : `${k === 'zoomLink' ? 'link' : 'contraseña'} → ${v}`)).join(' · ')}</span></>} />
              <Section title="Ya no existen en Zoom (marcar)" tone="text-red-600" items={diff.missing} idOf={m => m.entryId}
                selected={sel.missing} toggle={toggler('missing')}
                render={m => <><b>{kindLabel(m.kind)}</b> {m.name}</>} />
              <Section title="Están en Zoom pero no en el Gestor (importar)" tone="text-sky-600" items={diff.imports} idOf={i => i.zoomId}
                selected={sel.imports} toggle={toggler('imports')}
                render={i => <><b>{kindLabel(i.kind)}</b> {i.topic} <span className="block text-[11px] text-gray-500">{i.meetingId} · {i.zoomLink}</span></>} />
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cerrar</button>
          <button onClick={handleApply} disabled={!diff || (total === 0 && !diff.verified?.length) || applying}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
            {applying ? 'Aplicando…' : total === 0 ? 'Marcar como verificadas' : `Aplicar ${total} cambio(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ZoomSyncModal;
